import type { EventTarget } from './models/EventTarget'
import type { SchemaDefinition } from './models/SchemaDefinition'
import type { TypedDefinition } from './models/TypedDefinition'
import type { TypedEvent } from './models/TypedEvent'
import type { TypedNode } from './models/TypedNode'
import type { ViewModel } from './models/ViewModel'
import type { FormatType } from './plugins'
import type { Store } from './stores/Store'
import type { ObserveEvent } from '@rehaje/reactive'
import type {
  Disposer,
  Filter,
  Guard,
  KeyOf,
  Next,
  TClass,
  TRecord,
  TString,
  TWeakMap,
  ValueOf
} from '@rehaje/shared'

declare global {
  export interface RehajeExtendsSpecifics {}

  export interface RehajeMiddlewares {
    /**
     * 节点模型中间件
     * @param ctx
     * @param next
     */
    createTypedNode(
      ctx: CreateTypedNodeContext,
      next: Next<CreateTypedNodeContext>
    ): any
    /**
     * 视图组件中间件
     * @param component
     * @param next
     */
    createViewComponent(
      component: ViewComponent,
      next: Next<ViewComponent>
    ): any
    /**
     * 默认值合并中间件
     * @param ctx
     * @param next
     */
    defaultsMergeWithSchema(
      ctx: DefaultsMergeWithSchemaContext,
      next: Next<DefaultsMergeWithSchemaContext>
    ): any
    /**
     * Schema 推导中间件
     * @param ctx
     * @param next
     */
    inferOnlySchema(
      ctx: InferOnlySchemaContext,
      next: Next<InferOnlySchemaContext>
    ): TypedSchema
    /**
     * Schema 协同推导中间件
     * @param ctx
     * @param next
     */
    inferTypeScript(
      ctx: InferTypeScriptContext,
      next: Next<InferTypeScriptContext>
    ): string
    /**
     * Schema 协同推导中间件
     * @param ctx
     * @param next
     */
    inferWithSchema(
      ctx: InferWithSchemaContext,
      next: Next<InferWithSchemaContext>
    ): TypedSchema
    /**
     * Schema 类型转换中间件
     * @param ctx
     * @param next
     */
    transformWithSchema(
      ctx: TransformWithSchemaContext,
      next: Next<TransformWithSchemaContext>
    ): any
    /**
     * Schema 校验中间件
     * @param ctx
     * @param next
     */
    validateWithSchema(
      ctx: ValidateWithSchemaContext,
      next: Next<ValidateWithSchemaContext>
    ): Error[]
  }

  export interface RehajeSchemaDefinitions {}

  /**
   * ADT Schema 元数据类型
   */
  export interface RehajeSchemaMetadata extends TypedSpecificLike {
    /**
     * 是否废弃
     */
    deprecated?: boolean
    /**
     * 字段描述
     */
    description?: string
    /**
     * 示例用法
     */
    example?: string
    /**
     * 兜底值，主要覆盖类型转换
     */
    fallback?: any
    /**
     * 字段名
     */
    name?: string
    /**
     * 是否用 null 作为容错值
     */
    nullable?: boolean
    /**
     * 是否只读
     */
    readOnly?: boolean
    /**
     * 是否必填
     */
    required?: boolean
    /**
     * 标题
     */
    title?: string
    /**
     * 是否只写
     */
    writeOnly?: boolean
  }

  export interface RehajeSpecifics {
    Clipboard: ClipboardSpecific
    Schema: SchemaSpecific
    'Schema.Any': SchemaAnySpecific
    'Schema.Array': SchemaArraySpecific
    'Schema.BigInt': SchemaBigIntSpecific
    'Schema.Boolean': SchemaBooleanSpecific
    'Schema.Date': SchemaDateSpecific
    'Schema.DateTime': SchemaDateTimeSpecific
    'Schema.Dynamic': SchemaDynamicSpecific
    'Schema.Function': SchemaFunctionSpecific
    'Schema.Identifier': SchemaIdentifierSpecific
    'Schema.Intersection': SchemaIntersectionSpecific
    'Schema.Map': SchemaMapSpecific
    'Schema.Null': SchemaNullSpecific
    'Schema.Number': SchemaNumberSpecific
    'Schema.Object': SchemaObjectSpecific
    'Schema.Reference': SchemaReferenceSpecific
    'Schema.Set': SchemaSetSpecific
    'Schema.String': SchemaStringSpecific
    'Schema.TypedSchema': SchemaTypedSchemaSpecific
    'Schema.Undefined': SchemaUndefinedSpecific
    'Schema.Union': SchemaUnionSpecific
    'Schema.Unknown': SchemaUnknownSpecific
    'Schema.URLSearchParams': SchemaURLSearchParamsSpecific
    'Schema.Void': SchemaVoidSpecific
  }

  export interface RehajeTypedDefinitions {}

  export interface RehajeTypedEvents {}

  export interface RehajeTypedNodes {}

  export interface RehajeViewComponents {}

  export interface RehajeViewModels {}
}

/**
 * 事件触发阶段
 */
export enum EventPhase {
  /**
   * 默认阶段
   */
  NONE = 0,
  /**
   * 捕获阶段
   */
  CAPTURING_PHASE = 1,
  /**
   * 目标节点触发阶段
   */
  AT_TARGET = 2,
  /**
   * 冒泡阶段
   */
  BUBBLING_PHASE = 3
}

export interface AnalyzeInfo {
  addNodeInfos: [TypedNode, TypedNode, boolean][]
  removeNodes: TypedNode[]
  removeRoots: TypedNode[]
}

/**
 * Any 类型集合
 */
export type AnyTypedNode = TypedNode | ValueOf<RehajeTypedNodes>

export type AnyTypedSpecific =
  | ValueOf<RehajeExtendsSpecifics>
  | ValueOf<RehajeSpecifics>

export type AnyViewModel = ValueOf<RehajeViewModels> | ViewModel

/**
 * 回调型插件，可消费 Store
 */
export type CallablePlugin = (store: Store) => RehajePlugin | RehajePlugin[]

export interface CatchOptions {
  /**
   * 抛出错误事件，默认值为 true
   */
  dispatch?: boolean
  /**
   * 独立捕获，默认值为 false
   */
  isolated?: boolean
}

export interface Clipboard {
  /**
   * 复制，等价于
   * await clipboard.write(clipboard.toClipboard(...args))
   */
  copy(
    nodes?: TypedNode[],
    filter?: (node: TypedNode, type: SnapshotFilterType) => boolean
  ): Promise<void>
  /**
   * 导入剪切板协议
   */
  fromClipboard(
    clipboard: ClipboardSpecific,
    withDependencies?: boolean
  ): ImportedSnapshot | undefined
  origin: string
  /**
   * 粘贴，等价于
   * clipboard.fromClipboard(await clipboard.read(...args))
   */
  paste(withDependencies?: boolean): Promise<void>
  /**
   * 从浏览器剪切板中读取剪切板协议
   */
  read(): Promise<ClipboardSpecific | undefined>
  store?: Store
  /**
   * 导出剪切板协议
   */
  toClipboard(
    nodes?: TypedNode[],
    filter?: (node: TypedNode, type: SnapshotFilterType) => boolean
  ): ClipboardSpecific | undefined
  /**
   * 将剪切板协议写入到浏览器剪切板中
   */
  write(clipboard?: ClipboardSpecific): Promise<void>
}

export interface ClipboardSpecific extends TypedSpecificLike {
  migrationDefaults?: Record<string, PropertyKey[][]>
  origin: string
  snapshot: DependencySnapshot
  type: 'Clipboard'
}

export interface Collaborator extends TRecord {
  /**
   * 是否可重做
   */
  canRedo(): boolean
  /**
   * 是否可撤销
   */
  canUndo(): boolean
  /**
   * 启动响应式协同效果
   */
  effect(reactive?: any, options?: CollaboratorOptions): Disposer
  /**
   * 协同配置
   */
  options: CollaboratorOptions
  /**
   * 来源标记
   */
  origin?: any
  /**
   * reactive 响应式根
   */
  reactive?: any
  /**
   * 重做
   */
  redo(): void
  /**
   * 重做栈长度
   */
  redoStackLength: number
  /**
   * yjs 协同根
   */
  root?: any
  /**
   * 撤销
   */
  undo(): void
  /**
   * 撤销栈长度
   */
  undoStackLength: number
}

export interface CollaboratorOptions {
  /**
   * 调试空目标事件
   */
  onDebugEmptyTarget?: (type: string, event: any) => void
  /**
   * 调试事件监听器
   */
  onDebugEventListener?: (type: string, events: any[]) => void
  /**
   * YJS 文档变更监听器
   */
  onDocChanged?: DocChangedListener
  /**
   * Reactive 响应式变更监听器
   */
  onReactiveChanged?: ReactiveChangedListener
}

/**
 * createTypedNode 中间件上下文
 */
export interface CreateTypedNodeContext extends MiddlewareContext {
  /**
   * 父级建模节点
   */
  node?: TypedNode
  /**
   * 路径
   */
  path: PropertyKey[]
}

/**
 * createTypedNode 参数
 */
export interface CreateTypedNodeOptions {
  belongs?: PropertyKey[]
  defaultsMerge?: boolean
  parent?: TypedNode
}

export interface DefaultsMergeWithSchemaContext extends MiddlewareContext {
  /**
   * 父模型
   */
  definition: TypedDefinition
  /**
   * 是否初始化
   */
  initialize: boolean
  /**
   * 核心负载
   */
  payload: any
  /**
   * Schema
   */
  schema: TypedSchema
}

/**
 * 删除 Diff 项
 */
export type DeleteDifferent = {
  oldValue?: any
  path: PropertyKey[]
  type: 'delete'
}

/**
 * 依赖项
 */
export interface Dependency {
  /**
   * 被调用的方法名（仅当 relation='INVOCATION' 时可能存在）
   * 例如 `query.run()` -> invokedMethod='run'
   */
  invokedMethod?: string
  /**
   * 依赖变量名
   */
  name: string
  /**
   * 依赖节点
   */
  node?: TypedNode
  /**
   * 依赖路径（从实体 id 开始的属性链）
   */
  path: PropertyKey[]
  /**
   * 关系类型：INVOCATION（主动调用）或 DEPENDENCY（被动依赖）
   * 可选字段，未设置时上层可按 P3 规则兜底为 DEPENDENCY。
   */
  relation?: RelationType
}

/**
 * 依赖快照
 */
export interface DependencySnapshot {
  /**
   * 来源依赖节点 uniqueId 集合，非 sources 中的外部依赖
   */
  dependencies: string[]
  /**
   * 来源节点 uniqueId 差异集合，即不被依赖节点所包含的来源节点的集合
   * 在某种情况下，source 会被 dependency 所包含，在 differences 里需要排除掉
   * 可以理解为 differences = sources - dependencies
   *
   * 在仅粘贴本体时，应该插入 sources
   * 在携带依赖粘贴时，应该先插入 dependencies, 然后再插入 differences
   */
  differences: string[]
  /**
   * nodes 集合，包含依赖节点，key: 节点 uniqueId，value: 节点实例
   * 可以理解为 nodes = sources + dependencies
   */
  nodes: Record<string, SnapshotSpecificLike>
  /**
   * 来源节点 uniqueId 集合
   */
  sources: string[]
}

/**
 * Diff 项
 */
export type Different = DeleteDifferent | SetDifferent

export interface DocChangedListener {
  (events: any[], next: (events: any[]) => void, origin?: any): void
}

/**
 * addEffect 参数
 */
export interface EffectOptions {
  immediately?: boolean
  priority?: 'high' | 'normal'
}

/**
 * Error 类接口
 */
export interface ErrorConstructor<T = Error> {
  new (...args: any[]): T
  prototype: T
}

/**
 * 事件副作用钩子类型
 */
export type EventEffectHook<
  Factory extends TypedEventConstructor,
  Event extends TypedEvent = InstanceType<Factory>
> = Record<
  EventEffectHookTypes<Factory>,
  (listener?: EventListener<Event['currentTarget'], Event>) => void
>

/**
 * 事件副作用钩子类型集合
 */
export type EventEffectHookTypes<
  Factory extends TypedEventConstructor,
  Event extends InstanceType<Factory> = InstanceType<Factory>
> = `${Event['type']}Capture` | Event['type']

/**
 * 事件副作用执行器
 */
export type EventEffects = (ctx: any) => void

/**
 * 事件监听器
 */
export interface EventListener<
  Target extends EventTarget | undefined = EventTarget,
  Event extends TypedEvent = TypedEvent
> {
  (this: Target | undefined, event: Event): void
}

/**
 * 事件事务函数
 */
export type EventTransact = (dispatch: (event: TypedEvent) => void) => void

/**
 * ADT 中间件提取工具
 */
export type ExtractMiddleware<T extends MiddlewareTypes> =
  T extends KeyOf<RehajeMiddlewares> ? RehajeMiddlewares[T] : never

/**
 * ADT 协议类型提取工具
 */
export type ExtractSpecific<T extends SpecificTypes> =
  T extends KeyOf<RehajeSpecifics> ? RehajeSpecifics[T] : never

/**
 * ADT 定义提取工具
 */
export type ExtractTypedDefinition<T extends TypedDefinitionTypes> =
  T extends KeyOf<RehajeTypedDefinitions> ? RehajeTypedDefinitions[T] : never

/**
 * ADT 领域事件提取工具
 */
export type ExtractTypedEvent<T extends TypedEventTypes> =
  T extends KeyOf<RehajeTypedEvents> ? RehajeTypedEvents[T] : never

/**
 * 事件监听器提取工具
 */
export type ExtractTypedEventListener<
  T extends TypedEventTypes = TypedEventTypes
> = EventListener<EventTarget, ExtractTypedEvent<T>>

/**
 * ADT 节点提取工具
 */
export type ExtractTypedNode<T extends TypedNodeTypes> =
  T extends KeyOf<RehajeTypedNodes> ? RehajeTypedNodes[T] : never

export type ExtractTypes<T> = T extends { type: infer U } ? U : never

/**
 * ADT 组件类型提取工具
 */
export type ExtractViewComponent<T extends ViewComponentTypes> =
  T extends KeyOf<RehajeViewComponents> ? RehajeViewComponents[T] : never

/**
 * ADT 视图模型提取工具
 */
export type ExtractViewModel<T extends ViewModelTypes> =
  T extends KeyOf<RehajeViewModels> ? RehajeViewModels[T] : never

export interface ForkOptions {
  index?: number | number[]
  parent?: TypedNode
  variables?: TRecord
}

/**
 * 导入快照选项
 */
export interface FormSnapshotOptions {
  /**
   * 导入处理器，默认值：(node, root) => node.fromSnapshot(root)
   */
  resolver?: (node: TypedNode, root: TypedNode, previous?: TypedNode) => void
  /**
   * 是否导入依赖，如果有
   */
  withDependencies?: boolean
}

/**
 * ADT Function 类型
 */
export interface FunctionSpecific {
  /**
   * 函数体
   */
  body?: TypedSpecific
  /**
   * 参数类型
   */
  parameters?: any[]
  type: 'Function'
}

/**
 * 已导入快照节点实例集合
 */
export interface ImportedSnapshot {
  /**
   * 来源依赖节点实例集合
   */
  dependencies: TypedNode[]
  /**
   * 插入的所有新实例集合
   */
  nodes: TypedNode[]
  /**
   * 来源节点实例集合
   */
  sources: TypedNode[]
}

export interface InboundReferenceReport {
  /**
   * 2. 谁主动调用了我（入向调用）
   */
  calledBy: ReferenceNode[]
  /**
   * 1. 谁被动依赖了我（入向依赖）
   */
  referencedBy: ReferenceNode[]
}

/**
 * inferOnlySchema 中间件上下文
 */
export interface InferOnlySchemaContext extends MiddlewareContext {
  /**
   * 父模型
   */
  definition: TypedDefinition
  /**
   * 当前路径
   */
  path: PropertyKey[]
  /**
   * 核心负载
   */
  payload: TypedSchema
  /**
   * Schema
   */
  schema: TypedSchema | undefined
}

export interface InferTypeScriptContext extends MiddlewareContext {
  /**
   * 父模型
   */
  definition: TypedDefinition
  /**
   * 当前路径
   */
  path: PropertyKey[]
  /**
   * 核心负载
   */
  payload: TypedSchema
  /**
   * Schema
   */
  schema: TypedSchema
}

/**
 * inferWithSchema 中间件上下文
 */
export interface InferWithSchemaContext extends MiddlewareContext {
  /**
   * 父模型
   */
  definition: TypedDefinition
  /**
   * 当前路径
   */
  path: PropertyKey[]
  /**
   * 核心负载
   */
  payload: TypedSchema
  /**
   * Schema
   */
  schema: TypedSchema | undefined
  /**
   * 参考数据
   */
  target?: any
}

export interface InterfaceGenerateOptions {
  exported?: boolean
  generics?: string[]
}

/**
 * Rehaje 最底层抽象节点类型
 */
export interface InternalNode<T> {
  getChildren(): T[]
  getDescendants(): T[]
  getParent(): T | undefined
  getParents(): T[]
  getRoot(): T
  getStore(): Store
}

/**
 * 中间件通用上下文接口
 */
export interface MiddlewareContext {
  /**
   * 中间件核心负载
   */
  payload?: any
  /**
   * 状态中心
   */
  store: Store
}

/**
 * 中间件类型字符串集合
 */
export type MiddlewareTypes = KeyOf<RehajeMiddlewares> | TString

export type MigrationScript<T extends MigrationTypes = MigrationTypes> = (
  before: RehajeSpecifics[T],
  reportError: (message: string) => void
) => TypedSpecific

export type MigrationTypes = keyof RehajeSpecifics

export interface MoveNodeItem {
  belongs: PropertyKey[]
  property: any[]
  start: number
}

/**
 * 某个目标节点的四象限引用报告
 *
 * - referencedBy: 入向被动依赖（谁被动依赖了我）
 * - calledBy: 入向主动调用（谁主动调用了我）
 * - references: 出向被动依赖（我被动依赖了谁）
 * - calls: 出向主动调用（我主动调用了谁）
 */
export interface OutboundReferenceReport {
  /**
   * 4. 我主动调用了谁（出向调用）
   */
  calls: ReferenceNode[]
  /**
   * 3. 我被动依赖了谁（出向依赖）
   */
  references: ReferenceNode[]
}

/**
 * 环境变量
 */
export type PluginEnvironment = {
  get?: (this: Store) => any
  group?: string
  name: ((this: Store) => string) | string
  schema?: ((this: Store) => TypedSchema) | TypedSchema
  set?: (this: Store, value: any) => void
  value?: any
}

/**
 * 多态插件类型
 */
export type PluginLike = CallablePlugin | RehajePlugin

export type PluginMiddlewares = TransformMethodsToArrays<RehajeMiddlewares>

/**
 * 插件迁移脚本
 */
export type PluginMigrations = {
  [key in keyof RehajeSpecifics]?: VisitorMigrations<key>
}

/**
 * Rehaje 插件 ADT 访问器接口
 */
export interface PluginVisitors {
  components?: TRecord<undefined | ViewComponent>
  definitions?: TRecord<TypedDefinitionConstructor | undefined>
  models?: TRecord<undefined | ViewModelConstructor>
  nodes?: TRecord<TypedNodeConstructor | undefined>
}

export interface ReactiveChangedListener {
  (
    event: ObserveEvent,
    index: number,
    events: ObserveEvent[],
    next: (event: ObserveEvent) => void
  ): void
}

/**
 * 递归渲染函数
 */
export type RecursionRender = (node: TypedNode) => any

/**
 * 引用关系详情节点（以"源节点是谁"的视角描述一条引用）
 */
export interface ReferenceNode {
  /**
   * 引用在 DSL 中的路径，例如 ['page1', 'props', 'onClick', 'action', '0']
   *
   * 规范：通常由实体节点的 location
   * + 表达式/字段在实体中的相对路径拼接得到。
   */
  locationPath: string[]
  /**
   * 在表达式内部访问目标的成员链，例如：
   * - Mustache: ['text1', 'data']
   * - JavaScript: ['node', 'trigger']、['query1', 'data', 'map']
   *
   * 该成员链描述的是「代码层面如何访问目标节点」，适合用于：
   * - 调试 / 高亮具体成员位置；
   * - 在 AST 层面判定 Invocation / Dependency 时提供精细上下文；
   * - 代码重构时按标识符 + 成员链精确替换。
   */
  memberChain: string[]
  /**
   * 引用关系类型（主动调用 / 被动依赖）
   */
  relation: RelationType
  /**
   * 发起引用的实体节点 ID
   */
  sourceId: string
  /**
   * 发起引用的实体节点类型，例如 'Element.Button'、'Query.JSCode'
   */
  sourceType: string
  /**
   * 被引用的目标实体节点 ID
   */
  targetId: string
  /**
   * 被引用的目标实体节点类型
   */
  targetType: string
}

export interface ReferenceReport
  extends InboundReferenceReport, OutboundReferenceReport {}

/**
 * Rehaje 核心插件接口
 */
export interface RehajePlugin extends TRecord {
  environments?: PluginEnvironment[]
  middlewares?: PluginMiddlewares
  migrations?: PluginMigrations
  plugins?: PluginLike[]
  visitors?: PluginVisitors
}

/**
 * RehajePlugin 类接口
 */
export interface RehajePluginConstructor {
  new (): RehajePlugin
  prototype: RehajePlugin
}

/**
 * 引用关系类型
 *
 * - INVOCATION: 主动调用（触发目标节点的行为或方法，例如执行某个 Query/Action/组件方法）
 * - DEPENDENCY: 被动依赖（读取目标节点的数据或状态，例如 props 绑定、表达式读取变量等）
 */
export type RelationType = 'DEPENDENCY' | 'INVOCATION'

/**
 * 插件访问器
 */
export interface RequiredPluginVisitors {
  components: TRecord<ViewComponent>
  definitions: TRecord<TypedDefinitionConstructor>
  models: TRecord<ViewModelConstructor>
  nodes: TRecord<TypedNodeConstructor>
}

/**
 * 提取守卫函数和过滤函数返回值的类型工具
 */
export type Result<T = any> =
  T extends Guard<infer U> ? U : T extends Filter<infer U> ? U : T

/**
 * ADT 协议运行态类型
 */
export type RuntimeSchemaSpecific<T extends SchemaMetadataSpecific> = {
  [K in keyof T]?: Exclude<T[K], TypedSchema | TypedSpecific>
}

/**
 * ADT Schema Any 类型
 */
export interface SchemaAnySpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: any
  type: 'Schema.Any'
  /**
   * 具体值
   */
  value?: any
}

/**
 * ADT Schema Array 容器类型
 */
export interface SchemaArraySpecific extends SchemaMetadataSpecific {
  /**
   * 扩展项类型
   */
  additionalItems?: TypedSchema
  /**
   * 默认值
   */
  default?: any[] | TypedSpecific
  /**
   * 重载类型推导
   */
  infer?(
    this: TypedDefinition,
    target: any,
    infer: (
      target?: any,
      schema?: SchemaArraySpecific,
      path?: PropertyKey[]
    ) => SchemaArraySpecific
  ): SchemaArraySpecific
  /**
   * 数组项类型
   */
  items?: TypedSchema | TypedSchema[]
  /**
   * 最大项数
   */
  maxItems?: number | TypedSpecific
  /**
   * 最小项数
   */
  minItems?: number | TypedSpecific
  type: 'Schema.Array'
  /**
   * 具体值
   */
  value?: any[] | TypedSpecific
}

/**
 * ADT Schema BigInt 类型
 */
export interface SchemaBigIntSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: bigint | number | TypedSpecific
  /**
   * 最大值
   */
  maximum?: bigint | number | TypedSpecific
  /**
   * 最小值
   */
  minimum?: bigint | number | TypedSpecific
  type: 'Schema.BigInt'
  /**
   * 具体值
   */
  value?: bigint | number | TypedSpecific
}

/**
 * ADT Schema Boolean 类型
 */
export interface SchemaBooleanSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: boolean | TypedSpecific
  type: 'Schema.Boolean'
  /**
   * 具体值
   */
  value?: boolean | TypedSpecific
}

/**
 * ADT Schema Date 类型
 */
export interface SchemaDateSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: any
  type: 'Schema.Date'
  /**
   * 具体值
   */
  value?: any
}

/**
 * ADT Schema Date Time 类型
 */
export interface SchemaDateTimeSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: any
  type: 'Schema.DateTime'
  /**
   * 具体值
   */
  value?: any
}

/**
 * SchemaDefinition 类接口
 */
export interface SchemaDefinitionConstructor {
  new (): SchemaDefinition
  prototype: SchemaDefinition
}

/**
 * ADT Schema Dynamic 类型
 */
export interface SchemaDynamicSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: any
  infer?: (
    target: any,
    infer: (target: any, schema?: SchemaDynamicSpecific) => TypedSchema
  ) => TypedSchema
  test?: (
    value: any,
    target: any,
    test: (value: any, schema: TypedSchema) => boolean
  ) => boolean
  transform?: (
    value: any,
    target: any,
    transform: (value: any, schema: TypedSchema) => any
  ) => any
  type: 'Schema.Dynamic'
  /**
   * 具体值
   */
  value?: any
}

/**
 * ADT Schema Function 类型
 */
export interface SchemaFunctionSpecific extends SchemaMetadataSpecific {
  /**
   * 扩展参数类型
   */
  additionalParameters?: TypedSchema
  /**
   * 是否为 async 函数
   */
  async?: boolean | TypedSpecific
  /**
   * 默认值
   */
  default?: Function | TypedSpecific
  /**
   * 是否为 generator 函数
   */
  generator?: boolean | TypedSpecific
  /**
   * 参数类型
   */
  parameters?: TypedSchema | TypedSchema[]
  /**
   * 返回值类型
   */
  return?: TypedSchema
  type: 'Schema.Function'
  /**
   * 具体值
   */
  value?: Function | TypedSpecific
}

export interface SchemaIdentifierSpecific extends SchemaMetadataSpecific {
  type: 'Schema.Identifier'
  /**
   * 具体值
   */
  value?: string
}

/**
 * ADT Schema Intersection 类型
 */
export interface SchemaIntersectionSpecific extends SchemaMetadataSpecific {
  type: 'Schema.Intersection'
  /**
   * 相交类型集合
   */
  types?: (SchemaIntersectionSpecific | SchemaObjectSpecific)[]
  /**
   * 具体值，其实不存在，用来解决类型错误
   */
  value?: any
}

/**
 * ADT Schema Map 类型
 */
export interface SchemaMapSpecific extends SchemaMetadataSpecific {
  /**
   * 序列化数据
   */
  entries?: [any, any][]
  type: 'Schema.Map'
  /**
   * 具体值
   */
  value?: any | TypedSpecific
}

export type SchemaMetadataSpecific = RehajeSchemaMetadata

/**
 * ADT Schema Null 类型
 */
export interface SchemaNullSpecific extends SchemaMetadataSpecific {
  type: 'Schema.Null'
  /**
   * 具体值
   */
  value?: null | TypedSpecific
}

/**
 * ADT Schema Number 类型
 */
export interface SchemaNumberSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: number | TypedSpecific
  /**
   * 最大值
   */
  maximum?: number | TypedSpecific
  /**
   * 最小值
   */
  minimum?: number | TypedSpecific
  type: 'Schema.Number'
  /**
   * 具体值
   */
  value?: number | TypedSpecific
}

/**
 * ADT Schema Object 容器类型
 */
export interface SchemaObjectSpecific extends SchemaMetadataSpecific {
  /**
   * 扩展属性
   */
  additionalProperties?: TypedSchema
  /**
   * 默认值
   */
  default?: TRecord | TypedSpecific
  /**
   * 幂等，会下钻传染
   */
  idempotence?: boolean
  /**
   * 重载类型推导
   */
  infer?(
    this: TypedDefinition,
    target: any,
    infer: (
      target?: any,
      schema?: SchemaObjectSpecific,
      path?: PropertyKey[]
    ) => SchemaObjectSpecific
  ): SchemaObjectSpecific
  /**
   * 最大属性数
   */
  maxProperties?: number | TypedSpecific
  /**
   * 最小属性数
   */
  minProperties?: number | TypedSpecific
  /**
   * 属性
   */
  properties?: TypedSchema[]
  type: 'Schema.Object'
  /**
   * 具体值
   */
  value?: TRecord | TypedSpecific
}

export interface SchemaReferenceSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: any
  /**
   * 引用 id
   */
  ref: string
  type: 'Schema.Reference'
  /**
   * 具体值
   */
  value?: any
}

/**
 * ADT Schema Set 类型
 */
export interface SchemaSetSpecific extends SchemaMetadataSpecific {
  /**
   * 序列化数据
   */
  entries?: any[]
  type: 'Schema.Set'
  /**
   * 具体值
   */
  value?: any | TypedSpecific
}

/**
 * ADT Schema 最基础类型
 */
export interface SchemaSpecific extends SchemaMetadataSpecific {
  type: 'Schema'
}

/**
 * ADT Schema String 类型
 */
export interface SchemaStringSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: string | TypedSpecific
  /**
   * 格式校验，比如 email/url
   */
  format?: FormatType
  /**
   * 最大长度
   */
  maxLength?: number | TypedSpecific
  /**
   * 最小长度
   */
  minLength?: number | TypedSpecific
  /**
   * 正则校验
   */
  pattern?: string
  type: 'Schema.String'
  /**
   * 具体值
   */
  value?: string | TypedSpecific
}

export type SchemaTypedSchema = ValueOf<RehajeSpecifics> & {
  type: `Schema.${string}`
}

export interface SchemaTypedSchemaSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: TypedSchema | TypedSpecific
  type: 'Schema.TypedSchema'
  /**
   * 具体值
   */
  value?: TypedSchema | TypedSpecific
}

/**
 * ADT Schema Undefined 类型
 */
export interface SchemaUndefinedSpecific extends SchemaMetadataSpecific {
  type: 'Schema.Undefined'
  /**
   * 具体值
   */
  value?: TypedSpecific | undefined
}

/**
 * ADT Schema Union 容器类型
 */
export interface SchemaUnionSpecific extends SchemaMetadataSpecific {
  /**
   * 类型匹配器
   */
  target?: string | TypedSpecific
  type: 'Schema.Union'
  /**
   * 联合类型集合
   */
  types?: TypedSchema[]
  /**
   * 具体值，其实不存在，用来解决类型错误
   */
  value?: any
}

/**
 * ADT Schema Unknown 类型
 */
export interface SchemaUnknownSpecific extends SchemaMetadataSpecific {
  /**
   * 默认值
   */
  default?: TypedSpecific | unknown
  type: 'Schema.Unknown'
  /**
   * 具体值
   */
  value?: TypedSpecific | unknown
}

/**
 * ADT Schema Map 类型
 */
export interface SchemaURLSearchParamsSpecific extends SchemaMetadataSpecific {
  /**
   * 序列化数据
   */
  entries?: string[][]
  type: 'Schema.URLSearchParams'
  /**
   * 具体值
   */
  value?: any | TypedSpecific
}

/**
 * ADT Schema Void 类型
 */
export interface SchemaVoidSpecific extends SchemaMetadataSpecific {
  type: 'Schema.Void'
  /**
   * 合并类型集合
   */
  types?: TypedSchema[]
}

/**
 * 作用域声明接口, 供 scope 装饰器使用
 */
export interface ScopeDeclaration<T extends TClass = TClass> {
  type?: ScopeTypes
  variables?: ScopeVariableDeclarations<T>
}

/**
 * 作用域声明接口, 供 TypedNode 原型使用
 */
export interface ScopeDeclarations<T extends TClass = TClass> {
  type?: ScopeTypes
  variables?: ScopeVariableDeclarations<T>[]
}

/**
 * 作用域类型
 */
export type ScopeTypes = 'Block' | 'Local'

export type ScopeVariableDeclarations<T extends TClass = TClass> =
  | ((this: InstanceType<T>) => TypedVariableDeclaration<T>[])
  | TypedVariableDeclaration<T>[]

export type SearchMatch = {
  column: number
  end: number
  line: number
  start: number
  text: string
}

export type SearchOptions = {
  caseSensitive?: boolean
  searchText?: string
  useRegex?: boolean
  wholeWord?: boolean
}

export type SearchResultItem = {
  id: string
  matches: SearchMatch[]
  node: TypedNode
  path: PropertyKey[]
  previewText: string
  replace?: boolean
  searchText: string
}

/**
 * 赋值 Diff 项
 */
export type SetDifferent = {
  newValue?: any
  oldValue?: any
  path: PropertyKey[]
  type: 'set'
}

export type SnapshotFilterType = 'Dependency' | 'Source'

export type SnapshotRelation = Record<string, TypedNode>

/**
 * 快照通配类型接口
 */
export interface SnapshotSpecificLike extends TRecord, TypedSpecificLike {
  __r_order: number
  __r_snapshot_id: string
}

/**
 * ADT 协议类型字符串集合
 */
export type SpecificTypes = KeyOf<RehajeSpecifics> | TString

// ------------------------------ Middleware Types ------------------------------------------------------

// ------------------------------Event Types------------------------------------------------------

/**
 * createStore 参数
 */
export interface StoreOptions {
  /**
   * 复制粘贴引擎，只能传入 @rehaje/clipboard
   * 传入复制粘贴引擎可实现复制粘贴能力
   */
  clipboard?: Clipboard
  /**
   * 协同引擎，只能传入 @rehaje/collaborator
   * 传入协同引擎可实现协同能力
   */
  collaborator?: Collaborator
  /**
   * 关闭类型校验
   */
  disableTypeChecker?: boolean
  /**
   * 关闭类型转换能力，默认打开
   */
  disableTypeTransformer?: boolean
  /**
   * 事件 Hook 注入器
   * @param store
   * @returns
   */
  effects?: (store: Store) => void
  /**
   * 命名空间
   */
  namespace?: string
  /**
   * 插件
   */
  plugins?: PluginLike[]
  /**
   * 生产模式，发生系统任何错误都会被消除，但是只会报一个 warning，提示有错误，同时会正常发出 onError 事件
   */
  production?: boolean
  /**
   * 全局变量配置
   */
  variables?: TRecord
}

/**
 * StorePluginPrototype 原型接口
 */
export interface StorePluginPrototype {
  __r_storePluginMaps?: TWeakMap<RehajePlugin>
  getPlugin?(this: TypedDefinition | TypedNode | undefined | ViewModel): void
}

export type Transform<T, U extends { [k: string]: any }> = T extends {
  type: infer V
}
  ? V extends keyof U
    ? U[V]
    : T
  : T

export type TransformMethodsToArrays<T> = {
  [K in keyof T]?: T[K] | T[K][]
}

/**
 * transformWithSchema 中间件上下文
 */
export interface TransformWithSchemaContext extends MiddlewareContext {
  /**
   * 父模型
   */
  definition: TypedDefinition
  /**
   * 当前路径
   */
  path: PropertyKey[]
  /**
   * Schema
   */
  schema: TypedSchema | undefined
}

/**
 * TypedDefinition 类接口
 */
export interface TypedDefinitionConstructor {
  new (): TypedDefinition
  prototype: TypedDefinition
}

/**
 * ADT 定义类型字符串集合
 */
export type TypedDefinitionTypes = KeyOf<RehajeTypedDefinitions> | TString

/**
 * TypedEvent 类接口
 */
export interface TypedEventConstructor<T = any> {
  new (detail?: T): TypedEvent
  prototype: TypedEvent
}

/**
 * ADT 领域事件类型字符串集合
 */
export type TypedEventTypes = KeyOf<RehajeTypedEvents> | TString

/**
 * TypedNode 类接口
 */
export interface TypedNodeConstructor {
  new (specific: any): TypedNode
  prototype: TypedNode
}

/**
 * ADT 节点定义类型字符串集合
 */
export type TypedNodeTypes = KeyOf<RehajeTypedNodes> | TString

/**
 * ADT Schema 类型集合
 */
export type TypedSchema = SchemaMetadataSpecific & SchemaTypedSchema

export type TypedSchemaTypes = ExtractTypes<TypedSchema>

/**
 * ADT 协议类型集合
 */
export type TypedSpecific = AnyTypedSpecific | TypedSpecificLike

/**
 * ADT 通配类型接口
 */
export interface TypedSpecificLike {
  id?: string
  type: SpecificTypes
  version?: string
}

/**
 * 变量声明通用接口
 */
export interface TypedVariableDeclaration<T extends TClass = TClass> {
  /**
   * 变量描述
   */
  description?: string
  /**
   * 额外信息
   */
  extra?: TRecord
  /**
   * 变量取值函数
   * @param this
   * @returns
   */
  get?: (this: InstanceType<T>) => any
  /**
   * 变量分组
   */
  group?: string
  /**
   * 变量名
   */
  name: ((this: InstanceType<T>) => string) | string
  /**
   * 变量 schema
   */
  schema?: ((this: InstanceType<T>) => TypedSchema) | TypedSchema
  /**
   * 变量标题
   */
  title?: string
  /**
   * 变量作用域类型
   */
  type?: ScopeTypes
  /**
   * 变量值
   */
  value?: any
}

/**
 * validateWithSchema 中间件上下文
 */
export interface ValidateWithSchemaContext extends MiddlewareContext {
  /**
   * 父模型
   */
  definition: TypedDefinition
  /**
   * 父对象
   */
  parent: any
  /**
   * 当前路径
   */
  path: PropertyKey[]
  /**
   * 核心负载
   */
  payload: Error[]
  /**
   * 是否递归
   */
  recursion: boolean
  /**
   * Schema
   */
  schema: TypedSchema | undefined
  /**
   * 是否严格, 严格模式下，会进行校验必填项
   */
  strict?: boolean
  /**
   * 参考数据
   */
  target?: any
}

/**
 * 渲染组件接口，要求必须是函数
 */
export interface ViewComponent {
  (props: any): any
  displayName?: string | undefined // KEEP string | undefined!
}

/**
 * 渲染组件 Props 接口
 */
export interface ViewComponentProps<
  T extends TypedNode = TypedNode,
  U extends ViewModel = ViewModel
> {
  model?: U
  node: T
}

/**
 * 视图组件类型字符串集合
 */
export type ViewComponentTypes = KeyOf<RehajeViewComponents> | TString

/**
 * ViewModel 类接口
 */
export interface ViewModelConstructor<T extends ViewModel = ViewModel> {
  new (node: any): T
}

/**
 * ADT 视图模型类型字符串集合
 */
export type ViewModelTypes = KeyOf<RehajeViewModels> | TString

export type VisitorMigrations<T extends MigrationTypes = MigrationTypes> =
  Record<string, MigrationScript<T>>

export {
  type Middleware,
  type MiddlewareStartup,
  type Next
} from '@rehaje/shared'
